import {
  MenuItem,
  RadioGroup,
  TextField,
  Typography,
  capitalize,
} from '@mui/material';
import {
  OneRepMaxFormula,
  TeamDetail,
  zOneRepMaxFormula,
} from '@strength/domain';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DefaultWeightUnit } from '@strength/db-schema';
import { I18nTranslator } from '@strength/i18n';
import { trpc } from '../../../trpc/react';
import { LabeledRadioChip } from '../../components/input/labeled-radio-chip';
import { useViewer } from '../../context/viewer';
import { useToast } from '../../hooks/useToast';
import { LoadingPage } from '../loading';
import { TeamSettingItem } from './TeamSettingItem';

export const TeamSettingsPage = () => {
  const { t } = useTranslation();

  const teamDetailResult = trpc.roster.teamDetail.useQuery(undefined);

  const orgDetailResult = trpc.orgSettings.orgDetail.useQuery();

  if (teamDetailResult.data && orgDetailResult.data) {
    return (
      <TeamSettingsContent
        key={teamDetailResult.data.id}
        teamDetail={teamDetailResult.data}
        refetch={teamDetailResult.refetch}
      />
    );
  } else if (teamDetailResult.error || orgDetailResult.error) {
    return <div>{t('error')}</div>;
  } else {
    return <LoadingPage />;
  }
};

const TeamSettingsContent = ({
  teamDetail,
  refetch,
}: {
  teamDetail: TeamDetail;
  refetch: () => void;
}) => {
  const { t } = useTranslation();
  const {
    org: {
      settings: { showBaseLibrary },
    },
    hasPermissions,
  } = useViewer();
  const disableEdit = !hasPermissions('teams.settings', 'edit');

  const teamId = teamDetail.id;
  const [cycleLabel, setCycleLabel] = useState(
    teamDetail.settings.defaultCycleLabel,
  );
  const [sessionLabel, setSessionLabel] = useState(
    teamDetail.settings.defaultSessionLabel,
  );
  const [defaultWeightUnit, setDefaultWeightUnit] = useState(
    teamDetail.settings.defaultWeightUnit,
  );

  const [showWarningOnMissingActuals, setShowWarningOnMissingActuals] =
    useState(teamDetail.settings.warnOnMissingActuals);

  const [includeBaseLibraryExercises, setIncludeBaseLibraryExercises] =
    useState(teamDetail.settings.includeBaseLibraryExercises);

  const { toast } = useToast();
  const editTeamSettings = trpc.teamSettings.editTeamSettings.useMutation({
    onSuccess: () => {
      refetch();
      toast({
        title: t('teamSettingsUpdated'),
        severity: 'success',
      });
    },
    onError: () => {
      toast({
        title: t('somethingWentWrong'),
        severity: 'error',
      });
    },
  });

  const { settings } = teamDetail;

  return (
    <div className="p-6 flex flex-col mt-5 gap-y-4">
      <TeamSettingItem title={t('oneRepMaxCalculations')}>
        <div className="flex flex-row items-baseline gap-2">
          <TextField
            select
            label={t('formula')}
            value={settings.oneRepMaxFormula}
            disabled={disableEdit}
            onChange={(e) =>
              editTeamSettings.mutate({
                teamId,
                oneRepMaxFormula: e.target.value as OneRepMaxFormula,
              })
            }
          >
            {zOneRepMaxFormula.options.map((formula) => (
              <MenuItem key={formula} value={formula}>
                {capitalize(formula)}
              </MenuItem>
            ))}
          </TextField>
          <Typography>
            {settings.oneRepMaxFormula &&
              getFormulaDescription(settings.oneRepMaxFormula, t)}
          </Typography>
        </div>
      </TeamSettingItem>

      <TeamSettingItem title={t('blockDefaults')}>
        <div className="grid grid-cols-2 gap-2 items-end">
          <TextField
            disabled={disableEdit}
            label={t('cycleLabel')}
            value={cycleLabel}
            onChange={(e) => setCycleLabel(e.target.value)}
            onBlur={() => {
              if (cycleLabel !== teamDetail.settings.defaultCycleLabel) {
                editTeamSettings.mutate({
                  teamId,
                  defaultCycleLabel: cycleLabel,
                });
              }
            }}
          />
          <TextField
            disabled={disableEdit}
            label={t('sessionLabel')}
            value={sessionLabel}
            onChange={(e) => setSessionLabel(e.target.value)}
            onBlur={() => {
              if (sessionLabel !== teamDetail.settings.defaultSessionLabel) {
                editTeamSettings.mutate({
                  teamId,
                  defaultSessionLabel: sessionLabel,
                });
              }
            }}
          />
        </div>
      </TeamSettingItem>

      <TeamSettingItem title={t('defaultIntensityUnit')}>
        <RadioGroup
          row
          name="default-weight-unit"
          defaultValue=""
          value={defaultWeightUnit}
          onChange={(e) => {
            setDefaultWeightUnit(e.target.value as DefaultWeightUnit);
            editTeamSettings.mutate({
              teamId,
              defaultWeightUnit: e.target.value as DefaultWeightUnit,
            });
          }}
          sx={{ gap: 1 }}
        >
          <LabeledRadioChip
            key="lbs"
            value="lbs"
            label={t('poundsLbs')}
            disabled={disableEdit}
          />
          <LabeledRadioChip
            key="kg"
            value="kg"
            label={t('kilogramsKg')}
            disabled={disableEdit}
          />
        </RadioGroup>
      </TeamSettingItem>
      <TeamSettingItem title={t('incompleteActualsWarning')}>
        <RadioGroup
          row
          name="incomplete-actuals-warning"
          value={String(showWarningOnMissingActuals ?? false)}
          onChange={(e) => {
            const value = e.target.value === 'true';
            setShowWarningOnMissingActuals(value);
            editTeamSettings.mutate({
              teamId,
              warnOnMissingActuals: value,
            });
          }}
          sx={{ gap: 1 }}
        >
          <LabeledRadioChip
            key="true"
            value="true"
            label={t('showWarning')}
            disabled={disableEdit}
          />
          <LabeledRadioChip
            key="false"
            value="false"
            label={t('doNotShow')}
            disabled={disableEdit}
          />
        </RadioGroup>
      </TeamSettingItem>

      {showBaseLibrary && (
        <TeamSettingItem
          title={t('includeBaseLibraryExercisesInSearch')}
          description={t('includeBaseLibraryExercisesInSearchDescription')}
        >
          <RadioGroup
            row
            value={String(includeBaseLibraryExercises)}
            onChange={(e) => {
              const value = e.target.value === 'true';
              setIncludeBaseLibraryExercises(value);
              editTeamSettings.mutate({
                teamId,
                includeBaseLibraryExercises: value,
              });
            }}
            sx={{ gap: 1 }}
          >
            <LabeledRadioChip
              key="true"
              value="true"
              label={t('includeBaseLibrary')}
              disabled={disableEdit}
            />
            <LabeledRadioChip
              key="false"
              value="false"
              label={t('doNotIncludeBaseLibrary')}
              disabled={disableEdit}
            />
          </RadioGroup>
        </TeamSettingItem>
      )}
    </div>
  );
};

const getFormulaDescription = (
  formula: OneRepMaxFormula,
  t: I18nTranslator,
) => {
  switch (formula) {
    case 'brzycki':
      return t('formulaBrzycki');
    case 'epley':
      return t('formulaEpley');
    case 'lander':
      return t('formulaLander');
    case 'lombardi':
      return t('formulaLombardi');
    case 'mayhew':
      return t('formulaMayhew');
    case 'oconner':
      return t('formulaOconner');
    case 'wathan':
      return t('formulaWathan');
  }
};
